import type { IDWStation, IDWResult } from './types';
import { idwAt } from './idw';

export const CELL_SIZE = 24; // pixels

// SA approximate bounding box (used to fade particles out beyond the region
// where the station network gives meaningful interpolation).
const SA_MIN_LAT = -35;
const SA_MAX_LAT = -22;
const SA_MIN_LNG = 16;
const SA_MAX_LNG = 33;
const FADE_DIST = 2.5; // degrees — fade zone outside SA

/** Compute fade factor (0–1) based on distance from the SA bounding box. */
export function saFade(lat: number, lng: number): number {
  const dLat = Math.max(SA_MIN_LAT - lat, 0, lat - SA_MAX_LAT);
  const dLng = Math.max(SA_MIN_LNG - lng, 0, lng - SA_MAX_LNG);
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);
  if (dist <= 0) return 1;
  if (dist >= FADE_DIST) return 0;
  return 1 - dist / FADE_DIST;
}

export interface WindGrid {
  cells: IDWResult[];
  cols: number;
  rows: number;
  cellSize: number;
}

/**
 * Build a cached IDW grid for fast bilinear sampling during particle
 * animation. The grid covers the current map view in pixel space.
 * Ported from sites/my-fdi/src/utils/wind-field.ts.
 */
export function buildWindGrid(
  width: number,
  height: number,
  stations: IDWStation[],
  pixelToLatLng: (x: number, y: number) => { lat: number; lng: number },
): WindGrid {
  const cols = Math.ceil(width / CELL_SIZE) + 2;
  const rows = Math.ceil(height / CELL_SIZE) + 2;
  const cells: IDWResult[] = [];

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const { lat, lng } = pixelToLatLng(c * CELL_SIZE, r * CELL_SIZE);
      const cell = idwAt(lat, lng, stations);
      cell.fade = saFade(lat, lng);
      cells.push(cell);
    }
  }

  return { cells, cols, rows, cellSize: CELL_SIZE };
}

/** Bilinear sample the wind grid at a pixel position. */
export function sampleWindGrid(grid: WindGrid, x: number, y: number): IDWResult {
  const gx = x / grid.cellSize;
  const gy = y / grid.cellSize;
  const c0 = Math.floor(gx);
  const r0 = Math.floor(gy);
  const tx = gx - c0;
  const ty = gy - r0;
  const c1 = Math.min(c0 + 1, grid.cols - 1);
  const r1 = Math.min(r0 + 1, grid.rows - 1);

  const getCell = (r: number, c: number): IDWResult => {
    const idx = Math.max(0, r) * grid.cols + Math.max(0, c);
    return grid.cells[idx] ?? { u: 0, v: 0, speedKt: 0, fade: 0 };
  };

  const f00 = getCell(r0, c0);
  const f10 = getCell(r0, c1);
  const f01 = getCell(r1, c0);
  const f11 = getCell(r1, c1);

  const bilerp = (key: keyof IDWResult): number => {
    const v00 = f00[key];
    const v10 = f10[key];
    const v01 = f01[key];
    const v11 = f11[key];
    return v00 * (1 - tx) * (1 - ty) + v10 * tx * (1 - ty) + v01 * (1 - tx) * ty + v11 * tx * ty;
  };

  return {
    u: bilerp('u'),
    v: bilerp('v'),
    speedKt: bilerp('speedKt'),
    fade: bilerp('fade'),
  };
}
